import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Radar, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { API, INDUSTRIES, industryLabel } from './setupConstants';

// ── Auto-discover extensions from recent CDR data ─────────────────────────────
// Only useful when editing an existing domain that already has synced calls.
// Hits /api/admin/domains/{domainId}/discover-extensions and lets the admin
// bulk-tick newly seen extensions to add them to the monitoring list.

const formatLastSeen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const DiscoveredRow = ({ row, picked, classification, onToggle, onClassify }) => (
  <div
    className={`rounded-lg border transition-colors ${
      picked ? 'border-teal-500/50 bg-teal-500/5' : 'border-white/10 bg-white/[0.02]'
    }`}
  >
    <div className="flex items-center gap-3 px-4 py-2.5">
      <button
        onClick={() => onToggle(row.extension)}
        className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
          picked ? 'bg-teal-500 border-teal-500' : 'border-white/20 hover:border-white/40'
        }`}
        data-testid={`discover-pick-${row.extension}`}
      >
        {picked && <CheckCircle className="w-3.5 h-3.5 text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">
          {row.name || `Ext ${row.extension}`}
        </p>
        <p className="text-white/30 text-xs">
          Ext {row.extension} · {row.call_count} calls · last {formatLastSeen(row.last_seen)}
        </p>
      </div>
      {picked && (
        <select
          value={classification}
          onChange={e => onClassify(row.extension, e.target.value)}
          className="bg-[#0A0F1C] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-teal-500/50 min-w-[170px]"
          data-testid={`discover-type-${row.extension}`}
        >
          <option value="">Use domain default</option>
          {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
      )}
    </div>
    {picked && classification && (
      <div className="px-12 pb-2">
        <span className="text-xs bg-teal-500/10 text-teal-400 border border-teal-500/20 px-2 py-0.5 rounded">
          Coaching: {industryLabel(classification)}
        </span>
      </div>
    )}
  </div>
);

export const AutoDiscoverExtensions = ({
  domainId, token, monitoredExtensions, toggleExtension, setExtClassification, classifications,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discovered, setDiscovered] = useState(null); // null = not loaded yet
  const [days, setDays] = useState(30);
  // Extensions ticked during this discover session — kept visible in the panel
  // even after they enter `monitoredExtensions`, so the inline coaching-type
  // select stays reachable until the admin clicks "Save Changes".
  const [pickedThisSession, setPickedThisSession] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async (d = days) => {
    if (!domainId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/domains/${domainId}/discover-extensions?days=${d}`,
        { headers }
      );
      setDiscovered(r.data.discovered || []);
      if ((r.data.discovered || []).length === 0) {
        toast.info('No new extensions found in recent call data');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to discover extensions');
      setDiscovered([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && discovered === null) load();
  };

  const handlePick = (ext) => {
    toggleExtension(ext);
    setPickedThisSession(prev => {
      // If already in this session, the click is an untick → drop it.
      if (prev.includes(ext)) return prev.filter(e => e !== ext);
      // Otherwise this is a new add — keep the row visible until Save.
      return [...prev, ext];
    });
  };

  // Show: discovered rows that are either (a) not yet monitored, or
  // (b) were just picked in this session — so the inline coaching-type
  // select stays visible after a tick.
  const sessionSet = new Set(pickedThisSession);
  const visibleRows = (discovered || []).filter(
    r => !monitoredExtensions.includes(r.extension) || sessionSet.has(r.extension)
  );
  const newlyPicked = visibleRows.filter(r => sessionSet.has(r.extension)).length;
  const totalCount = (discovered || []).length;

  if (!domainId) return null;

  return (
    <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.03]">
      <button
        onClick={handleToggleOpen}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="auto-discover-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-400/15 flex items-center justify-center">
            <Radar className="w-4 h-4 text-amber-300" />
          </div>
          <div>
            <p className="text-white text-sm font-medium">Auto-discover unmonitored extensions</p>
            <p className="text-white/40 text-xs">
              {discovered === null
                ? 'Scan recent call data for extensions not yet on this list'
                : `${visibleRows.length} extension${visibleRows.length === 1 ? '' : 's'} found in the last ${days} days`}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5">
          <div className="flex items-center gap-2 pt-3">
            <span className="text-white/40 text-xs">Look back:</span>
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => { setDays(d); load(d); }}
                className={`px-2.5 py-1 rounded text-xs transition-colors ${
                  days === d
                    ? 'bg-amber-400/20 text-amber-300 border border-amber-400/30'
                    : 'bg-white/5 text-white/50 border border-white/10 hover:text-white/80'
                }`}
                data-testid={`discover-days-${d}`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={() => load(days)}
              disabled={loading}
              className="ml-auto px-3 py-1 text-xs text-amber-300 hover:text-amber-200 disabled:opacity-40"
              data-testid="discover-refresh"
            >
              {loading ? 'Scanning…' : 'Re-scan'}
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
            </div>
          )}

          {!loading && discovered !== null && visibleRows.length === 0 && (
            <p className="text-white/30 text-xs text-center py-4">
              No unmonitored extensions seen in the last {days} days. {totalCount > 0 && '(All discovered extensions are already on the list.)'}
            </p>
          )}

          {!loading && visibleRows.length > 0 && (
            <>
              <div className="flex items-center justify-between text-xs text-white/40">
                <span>
                  {visibleRows.length} new extension{visibleRows.length === 1 ? '' : 's'} found
                  {newlyPicked > 0 && ` · ${newlyPicked} just added`}
                </span>
                <button
                  onClick={() => visibleRows.forEach(r => {
                    if (!monitoredExtensions.includes(r.extension)) handlePick(r.extension);
                  })}
                  className="text-amber-300 hover:text-amber-200"
                  data-testid="discover-add-all"
                >
                  Add all
                </button>
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {visibleRows.map(row => (
                  <DiscoveredRow
                    key={row.extension}
                    row={row}
                    picked={monitoredExtensions.includes(row.extension)}
                    classification={classifications[row.extension] || ''}
                    onToggle={handlePick}
                    onClassify={setExtClassification}
                  />
                ))}
              </div>
              <p className="text-white/30 text-[11px]">
                Tip: ticked extensions are added immediately to your monitoring list — click <span className="text-white/60">Save Changes</span> at the bottom to persist.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
